import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LoginScreen } from './auth/LoginScreen';
import { loadSession, verifySession, type AdminSession } from './auth/auth';
import { Shell } from './components/Shell';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { LocationsPage } from './features/locations/LocationsPage';
import { TenantsPage } from './features/tenants/TenantsPage';
import { TenantDetailPage } from './features/tenants/TenantDetailPage';
import { LicensePackagesPage } from './features/license-packages/LicensePackagesPage';
import { OperatorsPage } from './features/operators/OperatorsPage';
import { AccountantsPage } from './features/accountants/AccountantsPage';
import { AdminUsersPage } from './features/users/AdminUsersPage';
import { AuditLogPage } from './features/audit/AuditLogPage';
import { ErrorReportsPage } from './features/errors/ErrorReportsPage';
import { FiscalCallsPage } from './features/fiscal-calls/FiscalCallsPage';
import { LpfrReleasesPage } from './features/lpfr-releases/LpfrReleasesPage';
import { PosReleasesPage } from './features/lpfr-releases/PosReleasesPage';
import { AssistantPage } from './features/assistant/AssistantPage';
import { ModulePricesPage } from './features/module-prices/ModulePricesPage';
import { PromoCampaignsPage } from './features/promo/PromoCampaignsPage';
import { AffiliatePage } from './features/affiliate/AffiliatePage';
import { PaymentsPage } from './features/payments/PaymentsPage';
import { SupportTicketsPage } from './features/support/SupportTicketsPage';
import { NotificationsPage } from './features/notifications/NotificationsPage';
import { SystemPage } from './features/system/SystemPage';
import { LegalDocumentsPage } from './features/legal/LegalDocumentsPage';
import { CompanySettingsPage } from './features/company/CompanySettingsPage';
import { ProfilePage } from './features/profile/ProfilePage';
import { UnitsPage } from './features/units/UnitsPage';
import { LocationLayoutPage } from './features/locations/LocationLayoutPage';
import { LocationKitchenPage } from './features/locations/LocationKitchenPage';
import { LocationDailyMenusPage } from './features/locations/LocationDailyMenusPage';
import { LocationReservationsPage } from './features/locations/LocationReservationsPage';
import { LocationStartersPage } from './features/locations/LocationStartersPage';
import { GuestCatalogPage } from './features/guest-catalog/GuestCatalogPage';
import { EFakturaPage } from './features/efaktura/EFakturaPage';
import { NewInvoicePage } from './features/efaktura/NewInvoicePage';
import { InvoiceDetailPage } from './features/efaktura/InvoiceDetailPage';
import { EOtpremnicaPage } from './features/eotpremnica/EOtpremnicaPage';
import { IncomingReceiptsPage } from './features/incoming-receipts/IncomingReceiptsPage';
import { NewDespatchAdvicePage } from './features/eotpremnica/NewDespatchAdvicePage';
import { DespatchDocumentDetailPage } from './features/eotpremnica/DespatchDocumentDetailPage';

export function App() {
  const [session, setSession] = useState<AdminSession | null>(loadSession);
  const [verifying, setVerifying] = useState(!!loadSession());

  useEffect(() => {
    // Verify cached session on mount - if token expired, verifySession() calls logout()
    // and returns null, then api client's 401 handler reloads the page.
    if (session) {
      verifySession()
        .then((verified) => {
          if (!verified) setSession(null);
        })
        .finally(() => setVerifying(false));
    }
  }, []);

  if (verifying) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#64748b' }}>
        Učitavanje...
      </div>
    );
  }

  if (!session) {
    return <LoginScreen onLogin={setSession} />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Shell session={session} />}>
          <Route index element={<DashboardPage />} />
          <Route path="locations" element={<LocationsPage />} />
          <Route path="locations/:id/layout" element={<LocationLayoutPage />} />
          <Route path="locations/:id/kitchen" element={<LocationKitchenPage />} />
          <Route path="locations/:id/menu" element={<LocationDailyMenusPage />} />
          <Route path="locations/:id/reservations" element={<LocationReservationsPage />} />
          <Route path="locations/:id/starters" element={<LocationStartersPage />} />
          <Route path="guest-catalog" element={<GuestCatalogPage />} />
          <Route path="operators" element={<OperatorsPage />} />
          <Route path="assistant" element={<AssistantPage />} />
          <Route path="accountants" element={<AccountantsPage />} />
          <Route path="users" element={<AdminUsersPage session={session} />} />
          <Route path="tenants" element={<TenantsPage />} />
          <Route path="tenants/:id" element={<TenantDetailPage />} />
          <Route path="license-packages" element={<LicensePackagesPage />} />
          <Route path="units" element={<UnitsPage />} />
          <Route path="efaktura" element={<EFakturaPage />} />
          <Route path="efaktura/invoices/new" element={<NewInvoicePage />} />
          <Route path="efaktura/invoices/:id" element={<InvoiceDetailPage />} />
          <Route path="incoming-receipts" element={<IncomingReceiptsPage />} />
          <Route path="eotpremnica" element={<EOtpremnicaPage />} />
          <Route path="eotpremnica/despatch-advices/new" element={<NewDespatchAdvicePage />} />
          <Route path="eotpremnica/documents/:id" element={<DespatchDocumentDetailPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="audit" element={<AuditLogPage />} />
          <Route path="errors" element={<ErrorReportsPage />} />
          <Route path="fiscal-calls" element={<FiscalCallsPage />} />
          <Route path="lpfr-releases" element={<LpfrReleasesPage />} />
          <Route path="pos-releases" element={<PosReleasesPage />} />
          <Route path="module-prices" element={<ModulePricesPage />} />
          <Route path="promo-campaigns" element={<PromoCampaignsPage />} />
          <Route path="affiliate" element={<AffiliatePage />} />
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="support" element={<SupportTicketsPage />} />
          <Route path="company" element={<CompanySettingsPage />} />
          <Route path="legal" element={<LegalDocumentsPage />} />
          <Route path="system" element={<SystemPage />} />
          <Route path="profile" element={<ProfilePage session={session} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
